<template>
    <SubLayout
        :pageTitle="title"
        :pageBreadcrumbs="breadcrumbs"
        :hasBack="false"
    >
      
      <template #layoutContent>
        <div class="card">
          <div class="card-header flex-wrap pt-6 pb-0">
            <div class="card-title">
              <div class="card-label">
                <h3 class="card-label mb-2">{{ $t('events.list') }}</h3>
              </div>
            </div>
            <div class="card-toolbar">
              <ButtonDropdownFilter
                :btnTitle="$t('events.all_status')"
                :search="false"
                :options="Object.values(eventStatus)"
                value-field="value"
                :name-field="locale"
                v-model="query.status"
              />
              <Button v-if="$can(abilities.CAN_CREATE_EVENT)" type="new" btn-class="btn-sm me-2" btn-text="events.create_event" @click="openCreateView"></Button>
            </div>
          </div>
          <div class="card-body">
            <table class="table align-middle table-row-bordered fs-6 gy-3 gx-4" id="events_datatable">
              <thead class="mb-5">
                <tr class="fw-bold fs-6" style="background-color: #F2F3F4;">
                  <th class="text-center">{{ $t("events.table.event") }} </th>
                  <th class="text-center">{{ $t('events.table.participant_number') }} </th>
                  <th class="text-center">{{ $t('events.table.start')}}</th>
                  <th class="text-center">{{ $t("events.table.status") }}</th>
                  <th class="text-center">{{ $t("events.table.actions") }}</th>
                </tr>
              </thead>
            </table>
          </div>
        </div>

        <createEventModal id="create_event_modal" @load="fetchDatas"/>
      </template>
    </SubLayout>
</template>
  
<script setup>
  import {computed, nextTick, onMounted, ref, watch} from "vue";
  import SubLayout from "@/views/Layouts/partials/SubLayout.vue";
  import {useStore} from "vuex";
  import Ls from "@/services/ls";
  import {BASE_URL} from "@/services/api";
  import {useRouter} from "vue-router";
  import {useLocales} from "@/composables/locales";
  import {useHasAbility} from "@/composables/hasAbility";
  import {useSwal} from "@/composables/useSwal";
  import ButtonDropdownFilter from "@/components/ButtonDropdownFilter.vue";
  import i18n from "@/plugins/i18n";
  import Button from "@/components/Button.vue";
  import createEventModal from "./components/modals/createEventModal.vue";
  import {Theme} from "@/composables/theming";
  import abilities from "@/composables/abilities";

  const router = useRouter()
  const store = useStore()
  const { can } = useHasAbility()
  const {locale, datatableLocaleUrl} = useLocales()
  
  const {showSwal} = useSwal()
  const title = ref("events.title")
  const breadcrumbs = ref([
    {
      name: 'events.list',
      route: 'events-index'
    }
  ])
  
  const table = ref('')
  const query = ref({
    status: '',
  })

  const eventStatus = computed(() => store.getters['events/getEventStatusEnum'])

  watch(() => query.value.status, (newValue) => {
    table.value.ajax.url(`${BASE_URL}/get/events/datatable?status=${query.value.status}`).load()
  })
  

  watch(() => locale.value, (value) => {
    nextTick(() => fetchDatas())
  }, { deep: true })

  function fetchDatas() {
    const AUTH_TOKEN = Ls.get('auth.token')

    table.value = $("#events_datatable").DataTable({
      destroy: true,
      responsive: !0,
      searchDelay: 500,
      processing: !0,
      serverSide: !0,
      pageLength: Ls.get('pageLength') ?? 5,
      lengthMenu: [5, 10, 25, 50, 75, 100 ],
      order: [[ 0, 'desc']],
      ajax: {
        url: BASE_URL + `/get/events/datatable?status=${query.value.status}`,
        type: "GET",
        beforeSend: function (request) {
          request.setRequestHeader("Authorization", "Bearer " + AUTH_TOKEN);
          request.setRequestHeader("Company", Ls.get('current.company'));
        }
      },
      columns: [
        { data: "title" }, { data: "participators_count" }, { data: "earliest_day" }, { data: "status" }, { data: "Actions", responsivePriority: -1 }
      ],
      columnDefs: [
        {
          targets: -5,
          order: "desc",
          className: "dt-center text-center clickable" ,
          render: function (t, a, e, l) {
            return '<span data-id="'+ e.id +'">' + t + "</span>";
          },
        },

        {
          targets: -4,
          order: "desc",
          className: "dt-center text-center clickable" ,
          render: function (t, a, e, l) {
            let nbr = t || 0
            return '<span data-id="'+ e.id +'">' + nbr + "</span>";
          },
        },

        {
          targets: -3,
          order: "desc",
          className: "dt-center text-center clickable" ,
          render: function (t, a, e, l) {
            return '<span data-id="'+ e.id +'">' + t + "</span>";
          },
        },

        {
          targets: -2,
          orderable: false,
          className: "dt-center text-center clickable" ,
          render: function (t, a, e, l) {
            let status = getEventStatusName(t)
            const locale = i18n.global.locale.value
            let color = status.color
            status = status[locale]
            return '<span class="badge badge-light-' + color +'" style="font-size:13px; margin:3px; padding:5px"><span style="margin-left: 5px" class="valueRow' + l.row + ' label label-success label-inline font-weight-bolder mr-2 edit_value_btn">' + status + '</span></span>'
          },
        },
        {
          width: "6%",
          targets: -1,
          "className": "dt-center text-start",
          title: "Actions",
          orderable: !1,
          render: (t, a, e, l) => {
            let render=""
            if(can(abilities.CAN_VIEW_EVENT)){
              render+='<a href="javascript:;" data-id="'+ e.id +'"  class="btn btn-sm btn-clean btn-icon show_btn" title="'+ i18n.global.t('button.show') +'"><i  data-id="'+ e.id +'" class="fas fa-eye fs-2" style="color: '+Theme.gray +'"></i></a>'
            }
            if(can(abilities.CAN_DELETE_EVENT)){
              render += '<a href="javascript:;" data-row="'+ l.row +'" data-id="'+ e.id +'" class="btn btn-sm btn-clean btn-icon delete_btn" title="'+ i18n.global.t('button.delete') +'"><i data-row="'+ l.row +'" data-id="'+ e.id +'" class="fas fa-trash fs-2" style="color: '+Theme.gray +'"></i></a>'
            }

            return render
          },
        },

      ],
      language: {
        url: datatableLocaleUrl.value,
      }
    });

    $(document).on("click", "#events_datatable .show_btn", (e) => {
      router.push({name: 'events-view', params: {id: e.target.getAttribute('data-id')}})
    });

    $(document).on("click", "#events_datatable .delete_btn", (e) => {
      deleteEvent(e.target.getAttribute('data-id'))
    });
  }

  function deleteEvent(id) {
    showSwal(
        i18n.global.t('events.swal.deleted'),
        i18n.global.t('events.swal.deleted_text')
    ).then( async (result) => {
      if (result.value) {
        store.dispatch('events/deleteEvent', id)
          .then(() => {
              window.toastr['success'](i18n.global.t('events.deleted_successfully'))
              table.value.ajax.reload()
          })
          .catch(() => {
              
          })
      }
    })
  }

  function getEventStatusName(status) {
    const {color, fr, en} = eventStatus.value?.find(el => el.value === status) || ''
    return {color, fr, en}
  }

  function handleRangeDate (range) {
    query.value.start_date = range.start_date
    query.value.end_date = range.end_date
  }

  function openCreateView() {
    $('#create_event_modal').modal('toggle')
  }

  onMounted( () => {
    Promise.all([
      store.dispatch('events/loadEventStatusEnum'),
      fetchDatas()
    ])
  })
</script>

<style scoped>

</style>